import { useState } from "react";
import { Dialog, DialogContent, DialogActions, Button, IconButton, Box, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import HelpOutlineRoundedIcon from "@mui/icons-material/HelpOutlineRounded";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import { colors } from "../colors";

export const useDialog = () => {
  const [open, setOpen] = useState(false);

  const [config, setConfig] = useState({
    type: "info",
    title: "",
    subtitle: "",
    message: "",
    onConfirm: null,
    onCancel: null,
    confirmLabel: null,
    cancelLabel: null,
  });

  const showDialog = ({ type = "info", title, subtitle = "", message, onConfirm, onCancel = null, confirmLabel = null, cancelLabel = null }) => {
    setConfig({
      type,
      title,
      subtitle,
      message,
      onConfirm,
      onCancel,
      confirmLabel,
      cancelLabel,
    });

    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleCancel = () => {
    if (config.onCancel) {
      config.onCancel();
    }

    setOpen(false);
  };

  const handleConfirm = () => {
    if (config.onConfirm) {
      config.onConfirm();
    }

    setOpen(false);
  };

  const typeConfig = {
    info: {
      color: colors.blue?.base ?? "#2563eb",
      bg: colors.blue?.[50] ?? "#eff6ff",
      icon: <InfoOutlinedIcon sx={{ fontSize: 19 }} />,
      buttonLabel: "Got it",
    },
    confirm: {
      color: colors.orange?.base ?? "#f97316",
      bg: colors.orange?.[50] ?? "#fff7ed",
      icon: <HelpOutlineRoundedIcon sx={{ fontSize: 19 }} />,
      buttonLabel: "Confirm",
    },
    warning: {
      color: colors.warning?.base ?? "#f59e0b",
      bg: colors.warning?.[50] ?? "#fffbeb",
      icon: <WarningAmberRoundedIcon sx={{ fontSize: 19 }} />,
      buttonLabel: "Ok",
    },
    success: {
      color: colors.success?.base ?? "#16a34a",
      bg: colors.success?.[50] ?? "#f0fdf4",
      icon: <CheckCircleOutlineRoundedIcon sx={{ fontSize: 19 }} />,
      buttonLabel: "Continue",
    },
  }[config.type];

  const DialogComponent = (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      sx={{
        zIndex: 999999,
        "& .MuiBackdrop-root": {
          backgroundColor: "rgba(15, 23, 42, 0.18)",
          backdropFilter: "blur(1px)",
        },
      }}
      PaperProps={{
        sx: {
          borderRadius: "18px",
          boxShadow: "0 18px 45px rgba(15, 23, 42, 0.14)",
          border: `1px solid ${colors.gray?.[100] ?? "var(--fx-surface-border, #E2E8F0)"}`,
          backgroundColor: "var(--fx-surface-base, #FFFFFF)",
          overflow: "hidden",
        },
      }}
    >
      <Box
        sx={{
          px: 2.5,
          pt: 2.25,
          pb: 1.5,
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 2,
          backgroundColor: "var(--fx-surface-base, #FFFFFF)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: "12px",
              backgroundColor: typeConfig.bg,
              color: typeConfig.color,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              mt: 0.15,
            }}
          >
            {typeConfig.icon}
          </Box>

          <Box>
            <Typography
              sx={{
                color: colors.gray?.[900] ?? "var(--fx-surface-text, #0F172A)",
                fontSize: "1rem",
                fontWeight: 800,
                lineHeight: 1.25,
              }}
            >
              {config.title}
            </Typography>

            {config.subtitle && (
              <Typography
                sx={{
                  mt: 0.35,
                  color: colors.gray?.[500] ?? "var(--fx-surface-textsecondary, #64748B)",
                  fontSize: "0.78rem",
                  fontWeight: 600,
                  lineHeight: 1.3,
                }}
              >
                {config.subtitle}
              </Typography>
            )}
          </Box>
        </Box>

        <IconButton
          onClick={handleClose}
          size="small"
          sx={{
            width: 28,
            height: 28,
            color: colors.gray?.[400] ?? "var(--fx-surface-texttertiary, #94A3B8)",
            borderRadius: "10px",
            "&:hover": {
              backgroundColor: colors.gray?.[50] ?? "var(--fx-surface-sunken, #F8FAFC)",
              color: colors.gray?.[700] ?? "var(--fx-surface-textsecondary, #64748B)",
            },
          }}
        >
          <CloseIcon sx={{ fontSize: 17 }} />
        </IconButton>
      </Box>

      <DialogContent
        sx={{
          px: 2.5,
          pt: 0.5,
          pb: 2,
          backgroundColor: "var(--fx-surface-base, #FFFFFF)",
        }}
      >
        <Typography
          sx={{
            color: colors.gray?.[300],
            fontSize: "0.88rem",
            fontWeight: 500,
            lineHeight: 1.55,
          }}
        >
          {config.message}
        </Typography>
      </DialogContent>

      <DialogActions
        sx={{
          px: 2.5,
          py: 2,
          gap: 1.25,
          justifyContent: "flex-end",
          backgroundColor: "var(--fx-surface-base, #FFFFFF)",
          borderTop: `1px solid ${colors.gray?.[100] ?? "var(--fx-surface-border, #E2E8F0)"}`,
        }}
      >
        {config.onCancel && (
          <Button
            onClick={handleCancel}
            variant="text"
            sx={{
              minHeight: 36,
              px: 2,
              borderRadius: "999px",
              color: colors.gray?.[600] ?? "var(--fx-surface-textsecondary, #64748B)",
              textTransform: "none",
              fontSize: "0.83rem",
              fontWeight: 800,
              "&:hover": {
                backgroundColor: colors.gray?.[50] ?? "var(--fx-surface-sunken, #F8FAFC)",
                color: colors.gray?.[800] ?? "var(--fx-surface-text, #0F172A)",
              },
            }}
          >
            {config.cancelLabel || "Cancel"}
          </Button>
        )}

        {config.onConfirm && (
          <Button
            onClick={handleConfirm}
            variant="contained"
            sx={{
              minHeight: 36,
              px: 2.25,
              borderRadius: "999px",
              bgcolor: typeConfig.bg,
              color: typeConfig.color,
              textTransform: "none",
              fontSize: "0.83rem",
              fontWeight: 900,
              boxShadow: "none",
              border: `1px solid ${typeConfig.bg}`,
              "&:hover": {
                bgcolor: typeConfig.bg,
                color: typeConfig.color,
                boxShadow: "none",
                filter: "brightness(0.98)",
              },
            }}
          >
            {config.confirmLabel || typeConfig.buttonLabel}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );

  return { showDialog, DialogComponent };
};
